import { defineStore } from 'pinia';
import type { Notification } from '~/types/Notification';

export const useNotificationsStore = defineStore('notifications', () => {
  // State
  const isDrawerOpen = ref(false);
  const position = ref<'top-right' | 'below-bell'>('top-right');
  const bellButtonRect = ref<DOMRect | null>(null);
  const notifications = ref<Notification[]>([]);
  const pagination = ref({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 9,
  });

  // Actions - only state management
  const openDrawer = () => {
    isDrawerOpen.value = true;
  };

  const closeDrawer = () => {
    isDrawerOpen.value = false;
  };

  const toggleDrawer = () => {
    isDrawerOpen.value = !isDrawerOpen.value;
  };

  const setPosition = (newPosition: 'top-right' | 'below-bell') => {
    position.value = newPosition;
  };

  const setBellButtonRect = (rect: DOMRect) => {
    bellButtonRect.value = rect;
  };

  const togglePosition = () => {
    position.value = position.value === 'top-right' ? 'below-bell' : 'top-right';
    console.log('🔄 Notification position changed to:', position.value);
  };

  // Set notifications from composable
  const setNotifications = (newNotifications: Notification[]) => {
    notifications.value = newNotifications;
  };

  // Update notifications list
  const updateNotificationList = (newNotifications: Notification[]) => {
    // Create a Set of existing notification IDs for faster lookup
    const existingIds = new Set(notifications.value.map((n) => n.id));

    // Filter out only the new notifications that don't exist
    const uniqueNewNotifications = newNotifications.filter((newNotification) => !existingIds.has(newNotification.id));

    // Append only the new unique notifications
    if (uniqueNewNotifications.length > 0) {
      notifications.value = [...notifications.value, ...uniqueNewNotifications];
    }
  };

  // Update notification in the store
  const updateNotification = (id: string | number, updates: Partial<Notification>) => {
    const index = notifications.value.findIndex((n) => n.id === id);
    if (index !== -1) {
      notifications.value[index] = { ...notifications.value[index], ...updates } as Notification;
    }
  };

  // Update pagination state
  const updatePagination = (newPagination: Partial<typeof pagination.value>) => {
    pagination.value = { ...pagination.value, ...newPagination };
  };

  // Getters
  const unreadCount = computed(() => {
    return notifications.value?.filter((n) => !n.read)?.length || 0;
  });

  const hasUnreadNotifications = computed(() => {
    return unreadCount.value > 0;
  });

  const modalStyle = computed(() => {
    if (position.value === 'below-bell' && bellButtonRect.value) {
      return {
        position: 'fixed',
        top: `${bellButtonRect.value.bottom + 8}px`,
        right: `${window.innerWidth - bellButtonRect.value.right}px`,
        left: 'auto',
        transform: 'none',
        maxWidth: '588px',
        width: 'calc(100vw - 32px)',
      };
    }

    // Default top-right positioning
    return {
      position: 'fixed',
      top: '80px',
      right: '16px',
      left: 'auto',
      transform: 'none',
      maxWidth: '588px',
      width: 'calc(100vw - 32px)',
    };
  });

  return {
    // State
    isDrawerOpen,
    notifications,
    position,
    bellButtonRect,
    pagination,

    // Actions
    openDrawer,
    closeDrawer,
    toggleDrawer,
    setPosition,
    setBellButtonRect,
    togglePosition,
    setNotifications,
    updateNotificationList,
    updateNotification,
    updatePagination,

    // Getters
    unreadCount,
    hasUnreadNotifications,
    modalStyle,
  };
});
